/**
 * @carc/protocol — the wire contract. [P2-msgs] [P2-events]
 *
 * ── How game state crosses the wire ──────────────────────────────────────────────────────────────
 *
 * It doesn't. We send `(seed, options, actions[])` and let the client rebuild the state by replaying
 * the log through the same pure engine the server used. That falls straight out of the engine being
 * deterministic, and it buys a lot:
 *
 *   - Payloads are tiny — an action is a few bytes, a full game state is not.
 *   - Client and server cannot disagree, because neither is describing the state to the other; they
 *     are both computing it from identical inputs with identical code.
 *   - Reconnect is trivial: send the log from the start.
 *   - `GameState` contains `Map`s and a union-find forest, none of which survive `JSON.stringify`
 *     without a bespoke serialiser that would then need its own correctness proof.
 *
 * The cost is a replay on the client — around 150 actions for a full game, which is microseconds.
 * `stateChecksum` rides along so a desync is loud rather than silent.
 */
import { z } from 'zod';
export const PROTOCOL_VERSION = 1;
// ── Rooms and seats ───────────────────────────────────────────────────────────────────────────────
export const MAX_SEATS = 6;
export const MIN_SEATS = 1;
/**
 * Six colours chosen to stay distinguishable under the common forms of colour blindness — the game is
 * literally played by identifying whose meeple is whose, so this is a correctness concern rather than
 * a decorative one.
 */
export const SEAT_COLOURS = [
    { id: 'crimson', hex: '#d1495b', name: 'Red' },
    { id: 'azure', hex: '#0f7bb5', name: 'Blue' },
    { id: 'saffron', hex: '#edae49', name: 'Yellow' },
    { id: 'forest', hex: '#2a9d4a', name: 'Green' },
    { id: 'plum', hex: '#8d5bab', name: 'Purple' },
    { id: 'slate', hex: '#4a4e69', name: 'Grey' },
];
// ── Client → server ───────────────────────────────────────────────────────────────────────────────
const posSchema = z.object({ x: z.number().int(), y: z.number().int() });
const rotationSchema = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);
const seatSchema = z.number().int().min(0).max(MAX_SEATS - 1);
export const gameActionSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('draw'), seat: seatSchema, stack: z.number().int().min(0).max(5).optional() }),
    z.object({ type: z.literal('place_tile'), seat: seatSchema, pos: posSchema, rotation: rotationSchema }),
    z.object({
        type: z.literal('place_meeple'),
        seat: seatSchema,
        featureIndex: z.number().int().min(0).max(15),
        // Optional so an action log written before meeple kinds existed still validates and replays.
        kind: z.enum(['follower', 'big', 'abbot', 'wagon', 'pig']).optional(),
    }),
    z.object({ type: z.literal('skip_meeple'), seat: seatSchema }),
]);
const roomOptionsSchema = z.object({
    stackCount: z.number().int().min(1).max(6).default(1),
    turnSeconds: z.number().int().min(0).max(600).default(0),
    packs: z.array(z.string().max(32)).max(8).default(['base']),
});
const playerNameSchema = z.string().trim().min(1).max(20);
export const clientMessageSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('create_room'),
        name: playerNameSchema,
        seatCount: z.number().int().min(MIN_SEATS).max(MAX_SEATS),
        options: roomOptionsSchema,
    }),
    z.object({
        type: z.literal('join_room'),
        code: z.string().trim().length(4),
        name: playerNameSchema,
        /** Presented on reconnect to reclaim seats without a fight. */
        token: z.string().max(64).optional(),
    }),
    z.object({ type: z.literal('claim_seat'), seat: seatSchema, name: playerNameSchema.optional() }),
    z.object({ type: z.literal('release_seat'), seat: seatSchema }),
    z.object({
        type: z.literal('set_seat_ai'),
        seat: seatSchema,
        ai: z.boolean(),
        difficulty: z.enum(['easy', 'steady', 'sharp']).optional(),
    }),
    z.object({ type: z.literal('start_game') }),
    z.object({ type: z.literal('action'), action: gameActionSchema }),
    z.object({ type: z.literal('resync') }),
    z.object({ type: z.literal('leave') }),
    z.object({ type: z.literal('ping') }),
]);
// ── Checksum ──────────────────────────────────────────────────────────────────────────────────────
/**
 * Fixed fold order for the per-kind meeple supply.
 *
 * The supply is a sparse object, and object key order is an implementation detail — two peers that
 * agree perfectly about the game could still hash it differently. Enumerating explicitly makes the
 * checksum depend on the values only. Adding a kind here changes the checksum for everyone, which is
 * correct: it is a protocol change.
 */
const SUPPLY_CHECKSUM_ORDER = ['follower', 'big', 'abbot', 'wagon', 'pig'];
/**
 * A cheap, order-sensitive fingerprint of the parts of the state a desync would corrupt.
 *
 * Not cryptographic and not trying to be — its only job is to make "the client and server disagree"
 * an immediate, visible failure instead of a slow drift nobody notices until the score is wrong.
 */
export function checksumState(state) {
    let h = 2166136261 >>> 0;
    const mix = (n) => {
        h ^= n >>> 0;
        h = Math.imul(h, 16777619) >>> 0;
    };
    const mixStr = (s) => {
        for (let i = 0; i < s.length; i++)
            mix(s.charCodeAt(i));
    };
    mix(state.board.size);
    for (const key of [...state.board.keys()].sort()) {
        const tile = state.board.get(key);
        if (!tile)
            continue;
        mixStr(key);
        mixStr(tile.defId);
        mix(tile.rotation);
    }
    for (const score of state.scores)
        mix(score + 1);
    // Fold the supply by kind, in a fixed order, so two peers agree byte for byte regardless of how
    // their objects happen to be keyed.
    for (const seatSupply of state.supply) {
        for (const kind of SUPPLY_CHECKSUM_ORDER)
            mix((seatSupply[kind] ?? 0) + 1);
    }
    mix(state.meeples.length);
    mixStr(state.phase);
    mix(state.activeSeat);
    return h >>> 0;
}
// ── Helpers ───────────────────────────────────────────────────────────────────────────────────────
/**
 * Room codes avoid `0/O` and `1/I`, because these get read aloud over the phone and typed by someone
 * squinting at a text message.
 */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export function isValidRoomCode(code) {
    return code.length === 4 && [...code].every((c) => ROOM_CODE_ALPHABET.includes(c));
}
export function parseClientMessage(raw) {
    const parsed = clientMessageSchema.safeParse(raw);
    return parsed.success
        ? { ok: true, message: parsed.data }
        : { ok: false, error: parsed.error.issues[0]?.message ?? 'invalid message' };
}
//# sourceMappingURL=index.js.map